/**
 * materializeCommissionPayouts.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Persist the commission split for a closed deal as rows in
 * `commission_payouts`. Up to v1, the Accounting page recomputed the
 * split at render time from the SPLIT constant + per-deal margin.
 * That worked fine for a live ledger but produced no record the
 * year-end 1099-NEC generator could roll up — the data only existed
 * inside the React component.
 *
 * This helper hoists the computation into a pure function so:
 *   1. The Accounting page can keep computing the split at render time
 *      (unchanged) for the live view.
 *   2. AppContext fires the materialize step ONCE when a deal flips to
 *      Funded, persisting one row per recipient.
 *   3. The unique index (deal_id, recipient_role, recipient_profile_id)
 *      makes re-runs idempotent — backfill of existing closed deals is
 *      safe to run on every deploy.
 *
 * The split is intentionally in-sync with src/pages/Accounting.jsx —
 * the SPLIT constant + named-recipient resolution match exactly. Any
 * future change to the split percentages MUST be made in both places.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { rollupEstimator, estimatorHasData } from './interestEstimator.js';
import { normalizeStage } from '../data/dealStages.js';
import { dealIsHeldByParaclete } from './assetManagement.js';
import {
  BUY_SIDE_COMMISSION_PCT,
  DISPOSITION_MANAGER_COMMISSION_PCT,
  OPERATING_FUND_COMMISSION_PCT,
  PARACLETE_BUY_SIDE_COMMISSION_PCT,
  SELL_SIDE_COMMISSION_PCT,
  buildStandardDealCommissionShares,
  buildParacleteBuyerCommissionShares,
  INVESTOR_REP_COMMISSION_PCT,
} from './coordinatorSplits.js';

// Mirrors src/pages/Accounting.jsx::SPLIT.
//
// disposition_manager is paid only on deals that name one, and to
// whoever the deal names — not to a fixed person the way the withdrawn
// global version was. Its 5% comes out of the operating fund's share,
// which buildStandardDealCommissionShares computes per deal, so the
// pct here is the ceiling rather than what every deal pays.
export const COMMISSION_SPLIT = [
  { key: 'buy_side_manager',   pct: BUY_SIDE_COMMISSION_PCT },
  { key: 'buy_side_coordinator', pct: null },
  { key: 'sell_side_manager',  pct: SELL_SIDE_COMMISSION_PCT },
  { key: 'firm_partner_brad',  pct: 0.10, firstName: 'brad' },
  { key: 'firm_partner_david', pct: 0.10, firstName: 'david' },
  { key: 'firm_partner_mays',  pct: 0.10, firstName: 'mays' },
  { key: 'disposition_manager', pct: DISPOSITION_MANAGER_COMMISSION_PCT },
  // The investor rep sits next to the sell-side manager because it
  // takes that slot: on an investor deal only one of the two ever
  // produces a row, and buildStandardDealCommissionShares zeroes the
  // other. Listing both keeps the ledger's role vocabulary honest
  // about which of them was paid.
  { key: 'investor_rep',       pct: INVESTOR_REP_COMMISSION_PCT },
  // Not a percentage — a flat amount off the top, which is why it
  // carries no pct. Listed here so the ledger's rows add up to the
  // deal's margin; without it the difference would have no name.
  { key: 'title_fee',          pct: null },
  { key: 'operating_fund',     pct: OPERATING_FUND_COMMISSION_PCT },
];

const CLOSED_STAGES = new Set(['Closed', 'Funded']);

// Same case-insensitive first-name match as Accounting.jsx so the
// materializer credits the same person the live page would.
//
// Exported under a long name because the Settlement Statement needs the
// SAME answer: it is the document the cheques are cut from, so the
// founder lines on it have to name whoever the materializer will write
// the payout rows to. A fourth private copy of this would be a fourth
// chance for the two to disagree.
export function matchCommissionRecipientByFirstName(users, firstName) {
  if (!firstName) return null;
  const target = firstName.toLowerCase();
  const candidates = (users || []).filter((u) => {
    const name = (u?.name || '').toLowerCase();
    const email = (u?.email || '').toLowerCase();
    if (!name && !email) return false;
    const firstToken = name.split(/\s+/)[0] || '';
    if (firstToken === target) return true;
    return email.startsWith(target + '.') || email.startsWith(target + '@');
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => {
    const aFirst = ((a.name || '').toLowerCase().split(/\s+/)[0]) === target ? 0 : 1;
    const bFirst = ((b.name || '').toLowerCase().split(/\s+/)[0]) === target ? 0 : 1;
    if (aFirst !== bFirst) return aFirst - bFirst;
    const aExact = (a.name || '').toLowerCase() === target ? 0 : 1;
    const bExact = (b.name || '').toLowerCase() === target ? 0 : 1;
    if (aExact !== bExact) return aExact - bExact;
    const ta = new Date(a.createdAt || 0).getTime() || 0;
    const tb = new Date(b.createdAt || 0).getTime() || 0;
    return ta - tb;
  });
  return candidates[0];
}

// Compute buyTotal / sellTotal for a deal — same logic
// Accounting.jsx uses, simplified to estimator-only because the
// ownership-based fallback needs the contacts/ownership graph that
// AppContext has but we'd rather not recompute server-side. v1
// requires a deal to have Estimator data before its split is
// materialized; deals that pre-date the estimator can be backfilled
// manually via the cap-table edit UI.
function computeMargin(deal) {
  const rows = Array.isArray(deal?.interestEstimates) ? deal.interestEstimates : [];
  if (!estimatorHasData(rows)) return null;
  const er = rollupEstimator(rows);
  const buyTotal = er.mi.buyTotal + er.wi.buyTotal;
  const sellTotal = er.mi.sellTotal + er.wi.sellTotal;
  // From the rollup, not re-derived. These rows are the PERSISTED payout
  // ledger, so a margin computed on a different basis here than on the
  // deal screen would be the version somebody gets paid on.
  return {
    buyTotal,
    sellTotal,
    soldBuyTotal: er.combinedSoldBuy || 0,
    retainedBuyTotal: er.combinedRetainedBuy || 0,
    margin: er.combinedMargin || 0,
  };
}

// Shared with Accounting via assetManagement so the live ledger and
// the persisted payout rows always agree on who the buyer is.
function isParacleteBuyer(deal, buyers = []) {
  return dealIsHeldByParaclete(deal, buyers);
}

// Returns the array of payout rows that should land in
// commission_payouts for the given deal. Empty array if the deal
// isn't eligible (not closed, no estimator, or zero margin).
export function buildCommissionPayoutRows(deal, { staffUsers = [], buyers = [], investors = [] } = {}) {
  if (!deal?.id) return [];
  if (!CLOSED_STAGES.has(normalizeStage(deal.stage))) return [];
  // Asset resales pay NO commissions. deal.asset_sale only exists once
  // a held asset's resale is activated — long after the original close
  // materialized its rows — so this guard can never skip an original
  // close. It exists because a resale re-closing the deal COULD
  // otherwise mint rows for roles assigned after the original close
  // (e.g. a sell-side agent added for the resale).
  if (deal.assetSale) return [];

  const margins = computeMargin(deal);
  if (!margins || margins.margin === 0) return [];
  const { margin } = margins;

  const paidOn = deal.sellClosingDate || deal.buyClosingDate || deal.closingDate || null;

  if (isParacleteBuyer(deal, buyers)) {
    // Self-purchased deals pay the buy-side agent once, here, at the
    // original Funded close. Reselling the held asset to an outside
    // buyer later (AUM resale flow) generates NO commission payouts —
    // resales never re-enter this materializer because the deal's
    // stage doesn't change, and the idempotency index would reject a
    // duplicate paraclete_buy_side_manager row anyway.
    // The coordinator is carved out of the 50% pool the same way they
    // are out of the 40% pool below, so a self-purchase pays them their
    // rate instead of nothing. The buy-side agent takes the remainder.
    const shares = buildParacleteBuyerCommissionShares(margin, deal, staffUsers);
    const rows = [];
    if (deal.buySideAgentId) {
      rows.push({
        dealId: deal.id,
        recipientRole: 'paraclete_buy_side_manager',
        recipientProfileId: deal.buySideAgentId,
        payoutAmount: shares.buySideAgent,
        paidOn,
      });
    }
    if (deal.coordinatorId && shares.coordinator) {
      rows.push({
        dealId: deal.id,
        recipientRole: 'buy_side_coordinator',
        recipientProfileId: deal.coordinatorId,
        payoutAmount: shares.coordinator,
        paidOn,
      });
    }
    // Every transaction, self-purchase included: the title work happens
    // either way. This branch returns before the loop below, so the row
    // is added here rather than inherited from it.
    if (shares.titleFee) {
      rows.push({
        dealId: deal.id,
        recipientRole: 'title_fee',
        recipientProfileId: null,
        payoutAmount: shares.titleFee,
        paidOn,
      });
    }
    return rows;
  }

  const namedRecipients = {};
  COMMISSION_SPLIT.forEach((s) => {
    if (!s.firstName) return;
    namedRecipients[s.key] = matchCommissionRecipientByFirstName(staffUsers, s.firstName);
  });

  const rows = [];
  const shares = buildStandardDealCommissionShares(margin, deal, staffUsers, { investors });
  for (const s of COMMISSION_SPLIT) {
    if (s.key === 'buy_side_manager') {
      if (!deal.buySideAgentId) continue;
      rows.push({
        dealId: deal.id, recipientRole: 'buy_side_manager',
        recipientProfileId: deal.buySideAgentId,
        payoutAmount: shares.buySide, paidOn,
      });
    } else if (s.key === 'buy_side_coordinator') {
      if (!deal.coordinatorId || !shares.coordinator) continue;
      rows.push({
        dealId: deal.id, recipientRole: 'buy_side_coordinator',
        recipientProfileId: deal.coordinatorId,
        payoutAmount: shares.coordinator, paidOn,
      });
    } else if (s.key === 'sell_side_manager') {
      // shares.sellSide is already 0 on an investor deal, but skipping
      // the row outright keeps a $0 line out of the ledger.
      if (!deal.sellSideAgentId || !shares.sellSide) continue;
      rows.push({
        dealId: deal.id, recipientRole: 'sell_side_manager',
        recipientProfileId: deal.sellSideAgentId,
        payoutAmount: shares.sellSide, paidOn,
      });
    } else if (s.key === 'investor_rep') {
      // No investor, no rep, no row — and the sell-side row above is
      // the one that pays in that case, because the rate came off the
      // same shares object. A placed deal whose investor has no rep
      // named pays nobody rather than paying the wrong person.
      if (!shares.investorRepId || !shares.investorRep) continue;
      rows.push({
        dealId: deal.id, recipientRole: 'investor_rep',
        recipientProfileId: shares.investorRepId,
        payoutAmount: shares.investorRep, paidOn,
      });
    } else if (s.key === 'disposition_manager') {
      // No manager named on the deal, no row — and the operating-fund
      // row below is a full 20% in that case, because the rate came
      // off the same shares object.
      if (!deal.dispositionManagerId || !shares.dispositionManager) continue;
      rows.push({
        dealId: deal.id, recipientRole: 'disposition_manager',
        recipientProfileId: deal.dispositionManagerId,
        payoutAmount: shares.dispositionManager, paidOn,
      });
    } else if (s.key === 'title_fee') {
      // Paid to the title function, not to a person — recipient_profile_id
      // is null, exactly as it is for the operating fund. A deal whose
      // margin could not cover it pays what there was, and a deal with
      // no margin pays nothing and mints no row.
      if (!shares.titleFee) continue;
      rows.push({
        dealId: deal.id, recipientRole: 'title_fee',
        recipientProfileId: null,
        payoutAmount: shares.titleFee, paidOn,
      });
    } else if (s.key === 'operating_fund') {
      rows.push({
        dealId: deal.id, recipientRole: 'operating_fund',
        recipientProfileId: null,
        payoutAmount: shares.operatingFund, paidOn,
      });
    } else {
      const u = namedRecipients[s.key];
      if (!u) continue;                                          // skip if Brad/David/Mays not on roster
      rows.push({
        dealId: deal.id, recipientRole: 'firm_partner',
        recipientProfileId: u.id,
        payoutAmount: shares[s.firstName], paidOn,
      });
    }
  }
  return rows;
}

// Which closed deals the ledger is missing rows for.
//
// `ledgerDealIds` is the set of deal ids that already hold at least one
// commission_payouts row. A deal is only a candidate when it holds NONE
// — a deal with a partial or hand-edited split is left alone, because
// inserting into it would mean deciding which of its rows is the real
// one, and that is not a decision a backfill button should make.
//
// Deals the materializer would write nothing for (no estimator, zero
// margin, an asset resale, a stage that is not closed) are not missing
// anything, so they are not listed either.
export function selectUnmaterializedDeals(deals, ledgerDealIds, deps = {}) {
  if (!Array.isArray(deals) || !(ledgerDealIds instanceof Set)) return [];
  return deals.filter((deal) => (
    !ledgerDealIds.has(deal?.id)
    && buildCommissionPayoutRows(deal, deps).length > 0
  ));
}

// Persist via the store. Idempotent — the unique index on
// commission_payouts catches dupes from re-runs.
export async function materializeCommissionPayouts(deal, deps, store) {
  const rows = buildCommissionPayoutRows(deal, deps);
  if (!rows.length) return [];
  return store.insertCommissionPayouts(rows);
}

// Buyer changed on an already-closed deal (e.g. flipped to Paraclete):
// the persisted split must be REBUILT, not appended — the sell-side
// row goes away and the buy agent moves to the 50%-of-margin
// Paraclete rate. Replace = delete the deal's rows, insert current.
export async function rematerializeCommissionPayouts(deal, deps, store) {
  if (typeof store.replaceCommissionPayoutsForDeal !== 'function') return [];
  const rows = buildCommissionPayoutRows(deal, deps);
  return store.replaceCommissionPayoutsForDeal(deal.id, rows);
}
